import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';

import {
  gray100,
  primaryButtonBackgroundColor,
  gray40,
  white,
  gray80,
  gray90,
} from '../../../constants/colors';
import type { ContactFormData } from '../../../types/contact';
import type { CompanyData } from '../../../types/company';
import type { InquiryCreateData } from '../../../types/inquiry';

import CustomSnackbar from '../../uiParts/form/CustomSnackbar';
import CustomLoading from '../../uiParts/form/CustomLoading';
import { AuthContext } from '../../../contexts/auth';
import { useAuthRedirect } from '../../../hooks/useAuthRedirect';
import MetaTitle from '../../utils/MetaTitle';
import SideBar from '../../uiParts/menu/SideBar';
import { useProxyAccessControl } from '../../../hooks/useProxyAccessControl';
import { usePageLayout } from '../../../hooks/usePageLayout';
import { createInquiry } from '../../../api/firebase/firestore/inquiry/create';
import { contactCategory } from '../../../constants/enum';
import { captureAsyncError } from '../../../utils/errorHandler';

const styles = {
  pageContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '32px 16px 16px',
    width: '100%',
    boxSizing: 'border-box',
  },
  modalContainer: {
    backgroundColor: white,
    borderRadius: '4px',
    border: `1px solid ${gray40}`,
    padding: '40px',
    width: '100%',
    maxWidth: '480px',
    boxSizing: 'border-box',
    position: 'relative',
    '@media screen and (max-width: 960px)': {
      padding: '32px 16px',
    },
  },
  modalHeader: {
    marginBottom: '24px',
    '@media screen and (max-width: 960px)': {
      marginBottom: '16px',
    },
  },
  modalTitle: {
    color: gray100,
    fontSize: '22px',
    fontWeight: '500',
    textAlign: 'left',
    '@media screen and (max-width: 960px)': {
      fontSize: '20px',
    },
  },
  infoSection: {
    marginBottom: '32px',
  },
  infoRow: {
    marginBottom: '24px',
    '@media screen and (max-width: 960px)': {
      marginBottom: '20px',
    },
  },
  infoLabel: {
    fontSize: '14px',
    fontWeight: '400',
    color: gray80,
    marginBottom: '8px',
    textAlign: 'left',
  },
  infoValue: {
    fontSize: '14px',
    color: gray100,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    textAlign: 'left',
  },
  buttonContainer: {
    display: 'flex',
    gap: '16px',
    marginTop: '40px',
    '@media screen and (max-width: 960px)': {
      flexDirection: 'column',
      gap: '12px',
      marginTop: '32px',
    },
  },
  button: {
    flex: 1,
    height: '44px',
    fontSize: '16px',
    fontWeight: '700',
  },
  cancelButton: {
    backgroundColor: white,
    color: gray90,
    border: `1px solid ${gray40}`,
    '&:hover': {},
  },
  submitButton: {
    backgroundColor: primaryButtonBackgroundColor,
    color: white,
    '&:hover': {
      backgroundColor: primaryButtonBackgroundColor,
      opacity: 0.8,
    },
  },
};

// カテゴリー名を取得する関数
const getCategoryName = (category: number): string => {
  switch (category) {
    case contactCategory.account:
      return 'アカウントに関すること';
    case contactCategory.plan:
      return '料金プランに関すること';
    case contactCategory.system:
      return 'システムに関すること';
    case contactCategory.other:
      return 'その他';
    default:
      return '';
  }
};

// カテゴリIDからURLパラメータに変換する関数
const getCategoryParam = (category: number): string => {
  switch (category) {
    case contactCategory.account:
      return 'account';
    case contactCategory.plan:
      return 'plan';
    case contactCategory.system:
      return 'system';
    case contactCategory.other:
      return 'other';
    default:
      return 'other';
  }
};

const ContactConfirm: React.FC = (): JSX.Element => {
  const [errorText, setErrorText] = useState('');
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const { user, isLoading } = useContext(AuthContext);
  const navigate = useNavigate();
  // 未認証ならログイン画面へ退避する（元URLは ?redirect= で引き継ぐ。#3203）
  useAuthRedirect();
  const location = useLocation();
  const { sideBarCollapsed, toggleSidebar, styles: layoutStyles } = usePageLayout();

  // 代理モード中はアクセス禁止
  useProxyAccessControl();

  // Contact画面から渡されたデータを取得
  const formData = location.state?.formData as ContactFormData | undefined;
  const companyData = location.state?.companyData as CompanyData | undefined;

  // データがない場合はContact画面へリダイレクト
  useEffect(() => {
    if (isLoading) return;

    if (user === null) {
      return;
    }

    if (!formData) {
      navigate('/contact?category=other');
    }
  }, [user, isLoading, formData, navigate]);

  const handleBack = (): void => {
    // 入力内容を保持して前の画面に戻る
    const categoryParam = formData ? getCategoryParam(formData.category) : 'other';
    navigate(`/contact?category=${categoryParam}`, { state: { formData, companyData } });
  };

  const handleSubmit = captureAsyncError(async (): Promise<void> => {
    if (!formData || !user || !user.company_id) {
      setErrorText('必要な情報が不足しています。');
      setOpen(true);
      return;
    }

    try {
      setSubmitting(true);

      // お問い合わせデータを作成
      const inquiryData: InquiryCreateData = {
        company_id: user.company_id,
        user_id: user.uid,
        category: formData.category,
        message: formData.message,
        contact_name: formData.name,
        contact_email: formData.email,
        contact_phone: formData.phoneNumber,
        company_name: formData.companyName,
        department: formData.department,
      };

      // Firestoreに保存
      await createInquiry(inquiryData);

      // 完了画面へ遷移
      navigate('/contact/complete', {
        state: { fromContact: true },
      });
    } catch (error: unknown) {
      setErrorText('送信に失敗しました。時間をおいて再度お試しください。');
      setOpen(true);
    } finally {
      setSubmitting(false);
    }
  });

  if (isLoading || !formData) {
    return (
      <Box sx={layoutStyles.pageWrapper}>
        <MetaTitle />
        <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
          <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
        </Box>
        <Box sx={[layoutStyles.pageContent, sideBarCollapsed && layoutStyles.pageContentCollapsed]}>
          <CustomLoading loading={true} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={layoutStyles.pageWrapper}>
      <MetaTitle />
      <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
        <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
      </Box>
      <Box sx={[layoutStyles.pageContent, sideBarCollapsed && layoutStyles.pageContentCollapsed]}>
        <Box sx={styles.pageContainer}>
          <Box sx={styles.modalContainer}>
            <Box sx={styles.modalHeader}>
              <Typography sx={styles.modalTitle}>お問い合わせ確認</Typography>
            </Box>

            {/* 問い合わせ者情報 */}
            <Box sx={styles.infoSection}>
              <Box sx={styles.infoRow}>
                <Typography sx={styles.infoLabel}>会社名</Typography>
                <Typography sx={styles.infoValue}>{formData.companyName}</Typography>
              </Box>
              <Box sx={styles.infoRow}>
                <Typography sx={styles.infoLabel}>部署名</Typography>
                <Typography sx={styles.infoValue}>{formData.department || '-'}</Typography>
              </Box>
              <Box sx={styles.infoRow}>
                <Typography sx={styles.infoLabel}>担当者名</Typography>
                <Typography sx={styles.infoValue}>{formData.name}</Typography>
              </Box>
              <Box sx={styles.infoRow}>
                <Typography sx={styles.infoLabel}>メールアドレス</Typography>
                <Typography sx={styles.infoValue}>{formData.email}</Typography>
              </Box>
              <Box sx={styles.infoRow}>
                <Typography sx={styles.infoLabel}>電話番号</Typography>
                <Typography sx={styles.infoValue}>{formData.phoneNumber}</Typography>
              </Box>
            </Box>

            {/* お問い合わせ内容 */}
            <Box>
              <Box sx={styles.infoRow}>
                <Typography sx={styles.infoLabel}>お問い合わせの種類</Typography>
                <Typography sx={styles.infoValue}>{getCategoryName(formData.category)}</Typography>
              </Box>
              <Box sx={{ ...styles.infoRow, marginBottom: 0 }}>
                <Typography sx={styles.infoLabel}>お問い合わせ内容</Typography>
                <Typography sx={styles.infoValue}>{formData.message}</Typography>
              </Box>
            </Box>

            {/* ボタン */}
            <Box sx={styles.buttonContainer}>
              <Button
                variant="outlined"
                sx={{ ...styles.button, ...styles.cancelButton }}
                onClick={handleBack}
                disabled={submitting}
              >
                修正する
              </Button>
              <Button
                variant="contained"
                sx={{ ...styles.button, ...styles.submitButton }}
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? '送信中...' : '送信する'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      <CustomSnackbar open={open} onClose={(): void => setOpen(false)} text={errorText} />
    </Box>
  );
};

export default ContactConfirm;
